import { shortLinkSyncTarget, inheritSyncTarget, urlParamsSyncTarget } from '../syncTargets';
import { FilterMergeStrategy } from '../enums';

import type { IFilterInheritState } from '@intelligence/models/filterInherit';
import type { IFilterValues } from '@intelligence/v3/filterCore/types';
import type { IFilterInitMeta, IFilterSyncTarget, IUseFilterablePageOptions, IResolvedLayer } from '../types';

export const isUrlParamsSyncEnabled = (config?: IUseFilterablePageOptions['syncTargetConfig']): boolean => {
  return config?.urlParams?.enabled === true;
};

/** 判断当前配置下短链同步是否启用，urlParams 开启时短链需要显式开启 */
export const isShortLinkSyncEnabled = (config?: IUseFilterablePageOptions['syncTargetConfig']): boolean => {
  if (config?.shortLink?.enabled === false) {
    return false;
  }

  if (isUrlParamsSyncEnabled(config)) {
    return config?.shortLink?.enabled === true;
  }

  return true;
};

/**
 * 获取可供当前页面消费的继承值。
 * 来源页与当前页相同时不消费，避免页面刷新时继承自己。
 */
export const getInheritedValuesByPageId = (pageId: string, inheritedState?: IFilterInheritState | null) => {
  const inheritedPageId = inheritedState?.inheritedPageId;
  if (!inheritedPageId || inheritedPageId === pageId) {
    return null;
  }

  return inheritedState?.inheritedValues || {};
};

/**
 * 根据 syncTargetConfig 生成内置同步目标列表。
 * 默认只启用 shortLink，inherit 和 urlParams 都需要显式开启。
 */
export const getDefaultSyncTargets = (config?: IUseFilterablePageOptions['syncTargetConfig']): IFilterSyncTarget[] => {
  const targets: IFilterSyncTarget[] = [];

  if (isShortLinkSyncEnabled(config)) {
    targets.push(shortLinkSyncTarget);
  }
  if (config?.inherit?.enabled) {
    targets.push(inheritSyncTarget);
  }
  if (config?.urlParams?.enabled) {
    targets.push(urlParamsSyncTarget);
  }

  return targets;
};

/**
 * 判断初始化完成后是否自动回写短链。
 * 自定义 syncTargets 代表页面完全接管同步策略，不再隐式写短链。
 */
export const shouldAutoSyncShortLinkOnInit = (params: {
  syncTargets?: IUseFilterablePageOptions['syncTargets'];
  syncTargetConfig?: IUseFilterablePageOptions['syncTargetConfig'];
}): boolean => {
  const { syncTargets, syncTargetConfig } = params;

  // 自定义 syncTargets 视为完全接管同步策略，初始化阶段不再隐式执行内置短链回写
  if (Array.isArray(syncTargets)) {
    return false;
  }

  return isShortLinkSyncEnabled(syncTargetConfig) && !isUrlParamsSyncEnabled(syncTargetConfig);
};

const hasResolvedFieldValue = (values: IFilterValues, field: string): boolean => {
  const value = values[field];

  return value !== undefined && value !== null;
};

const getLayerValues = (layer: IResolvedLayer): IFilterValues => {
  const values = layer?.values;

  if (Object.prototype.toString.call(values) !== '[object Object]') {
    return {};
  }

  return values;
};

const getResolvedFieldKeys = (values: IFilterValues): string[] => {
  return Object.keys(values).filter((field) => hasResolvedFieldValue(values, field));
};

const hasLayerResolvedValues = (layer: IResolvedLayer): boolean => {
  return getResolvedFieldKeys(getLayerValues(layer)).length > 0;
};

const assignLayerFieldOwners = (fieldOwners: Record<string, IResolvedLayer>, layer: IResolvedLayer): void => {
  const values = getLayerValues(layer);

  getResolvedFieldKeys(values).forEach((field) => {
    fieldOwners[field] = layer;
  });
};

const getResolvedFieldOwnerMap = (
  layers: IResolvedLayer[],
  mergeStrategy: FilterMergeStrategy,
): Record<string, IResolvedLayer> => {
  if (mergeStrategy === FilterMergeStrategy.TAKE_HIGHEST) {
    const sortedLayers = [...layers].sort((a, b) => b.priority - a.priority);
    const highestLayer = sortedLayers.find(hasLayerResolvedValues);

    if (!highestLayer) {
      return {};
    }

    const fieldOwners: Record<string, IResolvedLayer> = {};
    assignLayerFieldOwners(fieldOwners, highestLayer);
    return fieldOwners;
  }

  const sortedLayers = [...layers].sort((a, b) => a.priority - b.priority);
  const fieldOwners: Record<string, IResolvedLayer> = {};

  sortedLayers.forEach((layer) => {
    assignLayerFieldOwners(fieldOwners, layer);
  });

  return fieldOwners;
};

/**
 * 按优先级逐层合并：低优先级在前，高优先级在后。
 * 仅当某个 field 的值非 undefined/null 时才覆盖。
 */
const mergeByPriority = (defaultValues: IFilterValues, layers: IResolvedLayer[]): IFilterValues => {
  const sorted = [...layers].sort((a, b) => a.priority - b.priority);
  const result: IFilterValues = { ...defaultValues };

  sorted.forEach((layer) => {
    const values = getLayerValues(layer);

    Object.keys(values).forEach((field) => {
      const val = values[field];
      if (val !== undefined && val !== null) {
        result[field] = val;
      }
    });
  });

  return result;
};

/**
 * 取优先级最高的、返回非空结果的来源，
 * 与 defaultValues 合并。
 */
const takeHighest = (defaultValues: IFilterValues, layers: IResolvedLayer[]): IFilterValues => {
  const sorted = [...layers].sort((a, b) => b.priority - a.priority);

  for (const layer of sorted) {
    const values = getLayerValues(layer);
    const keys = getResolvedFieldKeys(values);
    if (keys.length > 0) {
      const result: IFilterValues = { ...defaultValues };
      keys.forEach((field) => {
        const val = values[field];
        result[field] = val;
      });
      return result;
    }
  }

  return { ...defaultValues };
};

/**
 * 根据合并策略合并默认值和多个来源层。
 * MERGE_BY_PRIORITY 按字段覆盖，TAKE_HIGHEST 只取最高优先级的非空来源。
 */
export const mergeFilterValues = (
  strategy: FilterMergeStrategy,
  defaultValues: IFilterValues,
  layers: IResolvedLayer[],
): IFilterValues => {
  if (strategy === FilterMergeStrategy.TAKE_HIGHEST) {
    return takeHighest(defaultValues, layers);
  }
  return mergeByPriority(defaultValues, layers);
};

/**
 * 生成初始化元信息，供 demo 或调试面板展示每个字段最终来自哪个来源。
 */
export const getInitMeta = (
  mergeStrategy: FilterMergeStrategy,
  layers: IResolvedLayer[],
  clearedSources: string[],
): IFilterInitMeta => {
  const sortedLayers = [...layers].sort((a, b) => a.priority - b.priority);
  const fieldOwners = getResolvedFieldOwnerMap(layers, mergeStrategy);
  const fieldSources = Object.keys(fieldOwners).reduce<Record<string, string>>((result, field) => {
    result[field] = fieldOwners[field].source.name;
    return result;
  }, {});

  return {
    mergeStrategy,
    layers: sortedLayers.map((layer) => {
      const values = getLayerValues(layer);

      return {
        name: layer.source.name,
        priority: layer.priority,
        fields: getResolvedFieldKeys(values),
      };
    }),
    fieldSources,
    clearedSources,
  };
};

/**
 * 判断某个来源消费后是否需要清理。
 * 只有该来源至少拥有一个最终生效字段时，才清理外部状态。
 */
export const shouldClearSourceAfterApply = (params: {
  mergeStrategy: FilterMergeStrategy;
  layers: IResolvedLayer[];
  targetLayer: IResolvedLayer;
}): boolean => {
  const { mergeStrategy, layers, targetLayer } = params;
  const fieldOwners = getResolvedFieldOwnerMap(layers, mergeStrategy);
  const targetValues = getLayerValues(targetLayer);

  return Object.keys(targetValues).some((field) => {
    return hasResolvedFieldValue(targetValues, field) && fieldOwners[field] === targetLayer;
  });
};
